import {
  Box,
  Flex,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RiAddCircleLine } from 'react-icons/ri';
import { IoSearch } from 'react-icons/io5';
import { FaUsers, FaFolder } from 'react-icons/fa';
import { MdPerson } from 'react-icons/md';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanLoading } from '../HelivanLoading';
import { MyAgentCard } from './MyAgentCard';
import { CreateAgentModal } from './CreateAgentModal';
import { CollectionsView } from './CollectionsView';
import { useGetMyCustomAgentsQuery, useGetMyAgentCollectionsQuery, CustomAgent } from '../../generated/graphql';

interface MyAgentsProps {
  embedded?: boolean;
  externalSearchTerm?: string;
  createModalDisclosure?: ReturnType<typeof useDisclosure>;
}

export const MyAgents = ({ embedded = false, externalSearchTerm, createModalDisclosure }: MyAgentsProps) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  // Persist the selected tab in the URL so it survives the page reload that org
  // switching triggers (GlobalTopbar reloads on org change), instead of snapping
  // back to "Mine" and forcing a re-click.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabKeys = ['mine', 'shared', 'collections'];
  const tabIndex = Math.max(0, tabKeys.indexOf(searchParams.get('agentsTab') || 'mine'));
  const setTabIndex = (i: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('agentsTab', tabKeys[i] ?? 'mine');
        return next;
      },
      { replace: true }
    );
  };
  const internalCreateModal = useDisclosure();
  // Use external controls when embedded, internal otherwise
  const searchTerm = embedded && externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const createModal = embedded && createModalDisclosure ? createModalDisclosure : internalCreateModal;

  // Agents I own in the current org.
  const { data: ownedAgentsData, loading: ownedLoading, refetch: refetchMyAgents } = useGetMyCustomAgentsQuery({
    variables: { ownedOnly: true },
  });

  // All agents available in the current org (homed here + org-shared). The "shared"
  // tab shows those I don't own.
  const { data: orgAgentsData, loading: sharedLoading } = useGetMyCustomAgentsQuery({
    variables: { ownedOnly: false },
  });

  // Query for collections
  const { data: collectionsData, loading: collectionsLoading } = useGetMyAgentCollectionsQuery();

  // Filter agents by search term
  const filterAgents = (agents: CustomAgent[] | undefined) => {
    if (!agents) return [];
    if (!searchTerm.trim()) return agents;
    const lowerSearch = searchTerm.toLowerCase();
    return agents.filter(
      (agent) =>
        agent.name.toLowerCase().includes(lowerSearch) ||
        agent.description?.toLowerCase().includes(lowerSearch)
    );
  };

  const ownedAgents = useMemo(
    () => filterAgents(ownedAgentsData?.getMyCustomAgents as CustomAgent[]),
    [ownedAgentsData, searchTerm]
  );

  // Agents in this org that I don't own (org-shared into my org).
  const sharedAgents = useMemo(
    () =>
      filterAgents(orgAgentsData?.getMyCustomAgents as CustomAgent[]).filter(
        (a) => !a.isOwner
      ),
    [orgAgentsData, searchTerm]
  );

  const handleAgentCreated = () => {
    refetchMyAgents();
  };

  const isLoading = ownedLoading || sharedLoading || collectionsLoading;
  const collectionsCount = collectionsData?.getMyAgentCollections?.length || 0;

  // Content that's shared between embedded and standalone modes
  const content = (
    <Stack spacing={4} width="full" maxWidth="1400px" height="fit-content">
      {/* Sub-tabs for My Agents section - scrollable on mobile */}
      <Box overflowX="auto" pb={1}>
        <HStack spacing={{ base: 1, sm: 2 }} minWidth="fit-content">
          <Box
            onClick={() => setTabIndex(0)}
            px={{ base: '6px', sm: '8px' }}
            py="4px"
            bg={tabIndex === 0 ? 'white' : 'transparent'}
            border="1px solid"
            borderColor={tabIndex === 0 ? HelivanColor.border.primary : 'transparent'}
            borderRadius="6px"
            _hover={{ bg: 'white' }}
            cursor="pointer"
            boxShadow={tabIndex === 0 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}
            whiteSpace="nowrap"
          >
            <HStack spacing={{ base: 1, sm: 2 }}>
              <MdPerson size={14} color={HelivanColor.text.blueMid} />
              <HelivanText variant="smallBody">Mine ({ownedAgentsData?.getMyCustomAgents?.length ?? 0})</HelivanText>
            </HStack>
          </Box>
          <Box
            onClick={() => setTabIndex(1)}
            px={{ base: '6px', sm: '8px' }}
            py="4px"
            bg={tabIndex === 1 ? 'white' : 'transparent'}
            border="1px solid"
            borderColor={tabIndex === 1 ? HelivanColor.border.primary : 'transparent'}
            borderRadius="6px"
            _hover={{ bg: 'white' }}
            cursor="pointer"
            boxShadow={tabIndex === 1 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}
            whiteSpace="nowrap"
          >
            <HStack spacing={{ base: 1, sm: 2 }}>
              <FaUsers size={14} color={HelivanColor.text.blueMid} />
              <HelivanText variant="smallBody">Shared ({sharedAgents.length})</HelivanText>
            </HStack>
          </Box>
          <Box
            onClick={() => setTabIndex(2)}
            px={{ base: '6px', sm: '8px' }}
            py="4px"
            bg={tabIndex === 2 ? 'white' : 'transparent'}
            border="1px solid"
            borderColor={tabIndex === 2 ? HelivanColor.border.primary : 'transparent'}
            borderRadius="6px"
            _hover={{ bg: 'white' }}
            cursor="pointer"
            boxShadow={tabIndex === 2 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}
            whiteSpace="nowrap"
          >
            <HStack spacing={{ base: 1, sm: 2 }}>
              <FaFolder size={14} color={HelivanColor.text.blueMid} />
              <HelivanText variant="smallBody">Collections ({collectionsCount})</HelivanText>
            </HStack>
          </Box>
        </HStack>
      </Box>

      {/* Tab content */}
      {tabIndex === 0 && (
        ownedAgents.length === 0 ? (
          <Flex
            direction="column"
            alignItems="center"
            justifyContent="center"
            py={16}
            gap={2}
          >
            <Flex
              as="button"
              w="56px"
              h="56px"
              borderRadius="16px"
              bg="#00477D"
              align="center"
              justify="center"
              mb={2}
              cursor="pointer"
              transition="all 0.15s"
              boxShadow="0 2px 8px rgba(0,71,125,0.3)"
              _hover={{
                bg: '#003d6b',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
              }}
              _active={{ transform: 'scale(0.96)' }}
              onClick={createModal.onOpen}
            >
              <RiAddCircleLine size={26} color="white" />
            </Flex>
            <Text fontSize="16px" fontWeight="600" color="#1a1a2e">
              No agents yet
            </Text>
            <Text fontSize="13px" color="#64748b">
              Connect your first agent to start creating watches
            </Text>
          </Flex>
        ) : (
          <Grid
            templateColumns={[
              'repeat(1, 1fr)',
              'repeat(2, 1fr)',
              'repeat(2, 1fr)',
              'repeat(3, 1fr)',
              'repeat(4, 1fr)',
              'repeat(5, 1fr)',
            ]}
            gap={{ base: 3, md: 5 }}
            width="full"
          >
            {ownedAgents.map((agent) => (
              <MyAgentCard
                key={agent.id}
                agent={agent}
                onUpdate={() => refetchMyAgents()}
              />
            ))}
          </Grid>
        )
      )}

      {tabIndex === 1 && (
        sharedAgents.length === 0 ? (
          <Flex
            direction="column"
            alignItems="center"
            justifyContent="center"
            py={10}
            gap={4}
          >
            <HelivanText variant="largeBody" colorOverride={HelivanColor.text.blueMid}>
              No agents have been shared with you yet
            </HelivanText>
          </Flex>
        ) : (
          <Grid
            templateColumns={[
              'repeat(1, 1fr)',
              'repeat(2, 1fr)',
              'repeat(2, 1fr)',
              'repeat(3, 1fr)',
              'repeat(4, 1fr)',
              'repeat(5, 1fr)',
            ]}
            gap={{ base: 3, md: 5 }}
            width="full"
          >
            {sharedAgents.map((agent) => (
              <MyAgentCard
                key={agent.id}
                agent={agent}
                onUpdate={() => refetchMyAgents()}
              />
            ))}
          </Grid>
        )
      )}

      {tabIndex === 2 && (
        <CollectionsView searchTerm={searchTerm} />
      )}
    </Stack>
  );

  // When embedded, return just the content (parent handles the wrapper)
  if (embedded) {
    if (isLoading && !ownedAgentsData) {
      return <HelivanLoading />;
    }

    return (
      <>
        <CreateAgentModal
          isOpen={createModal.isOpen}
          onClose={createModal.onClose}
          onSuccess={handleAgentCreated}
        />
        {content}
      </>
    );
  }

  // Standalone mode - full page layout
  if (isLoading && !ownedAgentsData) {
    return <HelivanLoading />;
  }

  return (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      <CreateAgentModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onSuccess={handleAgentCreated}
      />

      {/* Header */}
      <Flex
        width="full"
        direction={{ base: 'column', md: 'row' }}
        alignItems={{ base: 'stretch', md: 'center' }}
        py={{ base: '8px', md: '12px' }}
        px={{ base: '12px', sm: '16px', md: '24px' }}
        bg={HelivanColor.background.primary}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        justifyContent="space-between"
        gap={{ base: 3, md: 4 }}
        flexShrink={0}
      >
        {/* Row 1: Title + Create button */}
        <Flex width="full" alignItems="center" justifyContent="space-between">
          <HStack spacing={6}>
            <HelivanText variant="sectionHeader">Agents</HelivanText>
          </HStack>

          {/* Create button */}
          <HStack spacing={3}>
            <HelivanButton onClick={createModal.onOpen} variant="green" size="sm">
              <HStack spacing={1}>
                <RiAddCircleLine color="white" size={14} />
                <HelivanText colorOverride="white" variant="smallBody" display={{ base: 'none', sm: 'block' }}>
                  Add Agent
                </HelivanText>
              </HStack>
            </HelivanButton>
          </HStack>
        </Flex>

        {/* Row 2: Search bar */}
        <Box width={{ base: 'full', md: 'auto' }} flex={{ md: '1' }} maxWidth={{ md: '400px' }}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <IoSearch size={16} color={HelivanColor.text.blueMid} />
            </InputLeftElement>
            <Input
              bg={HelivanColor.background.secondary}
              borderRadius="6px"
              border="none"
              placeholder="Search..."
              _placeholder={{ color: HelivanColor.text.blueMid }}
              textColor="black"
              value={searchTerm}
              onChange={(e) => setInternalSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Box>
      </Flex>

      {/* Content */}
      <Flex
        flex="1"
        overflowY="auto"
        bg={HelivanColor.background.secondary}
        p={{ base: 3, sm: 4, md: 6 }}
        justifyContent="center"
      >
        {content}
      </Flex>
    </Stack>
  );
};
